import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, Sun, Video } from 'lucide-react';

// Scene Edit helpers (Oct 17 2026), server sceneShot.js + sceneSets.js:
//  - ShootCoachCard: how to shoot a clip the edit can keep you in.
//  - ShotCheckCard: free check of the start frame (face, size, angle, sharpness,
//    exposure) and which side the key light is on. Warns, never blocks.
//  - LightConflictNote: the prompt puts the main light on the other side of the face.
//  - SavedSetsPicker / SaveSetForm: keep a setting and reuse it on the next shot.

export type LightSide = 'left' | 'right' | 'front';
export type ShotResult = {
  checks: { key: string; ok: boolean; fix?: string }[];
  light: { side: LightSide; contrast: number } | null;
  faces?: number | null;
  error?: string;
};
export type SceneSet = { id: string; name: string; prompt: string; promptMode: 'room' | 'look'; lightSide: LightSide | null; thumbUrl: string };

const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
  minHeight: 44, padding: '10px 16px', borderRadius: 999, border: primary ? 0 : '1px solid var(--hj-hairline)',
  background: primary ? 'var(--hj-yellow)' : 'transparent', color: primary ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
  fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  display: 'inline-flex', alignItems: 'center', gap: 6,
});
const textBtn: React.CSSProperties = {
  minHeight: 36, padding: '6px 8px', border: 0, background: 'transparent', color: 'var(--hj-ink)',
  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'underline',
};
const field: React.CSSProperties = {
  width: '100%', minHeight: 44, padding: '0 12px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)',
  background: 'var(--hj-surface-1)', color: 'var(--hj-ink)', fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box',
};

// Sides are as the viewer sees the picture (the server's "viewer's left").
export const lightPhrase = (side: LightSide) => (side === 'front' ? 'from the front' : `from the ${side} of the frame`);

// ===== Coaching =====
const COACHING = [
  'Camera at eye level, about an arm and a half away, chest up in frame.',
  'Face a window or a lamp so the light is on your face, not behind you.',
  'A plain wall behind you works best. Nothing moving back there.',
  'Lock the camera off (tripod or propped up). No handheld drift.',
  'Hold still for 2 seconds before you start talking.',
];
export const ShootCoachCard: React.FC = () => (
  <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-deep)' }}>
    <div className="flex items-center gap-2" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-beige-ink, var(--hj-ink))', marginBottom: 8 }}>
      <Video size={16} strokeWidth={1.8} /> Shooting for Scene Edit
    </div>
    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.5, color: 'var(--hj-beige-ink, var(--hj-ink))' }}>
      {COACHING.map(c => <li key={c}>{c}</li>)}
    </ul>
  </div>
);

// ===== Shot check =====
const CHECK_LABELS: Record<string, string> = {
  face: 'Face found', size: 'Face big enough', angle: 'Facing the camera', sharp: 'Sharp', exposure: 'Exposure',
};
export const ShotCheckCard: React.FC<{ checking: boolean; result: ShotResult | null; at: string }> = ({ checking, result, at }) => {
  if (!checking && (!result || result.error || !result.checks.length)) return null;
  const bad = result?.checks.filter(c => !c.ok) || [];
  return (
    <div role="status" style={{ marginBottom: 18, padding: '12px 14px', borderRadius: 'var(--hj-r-md)', background: bad.length ? 'var(--hj-yellow-tint)' : 'var(--hj-surface-2)', border: `1px solid ${bad.length ? 'var(--hj-yellow)' : 'var(--hj-hairline-soft)'}` }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: checking ? 0 : 6 }}>
        {checking ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking your shot at {at}…</span>
          : bad.length ? `Shot check at ${at}: ${bad.length === 1 ? 'one thing' : `${bad.length} things`} to fix` : `Shot check at ${at}: good to go`}
      </div>
      {!checking && result && (
        <>
          {bad.length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {bad.map(c => (
                <li key={c.key} className="flex items-start gap-2" style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
                  <span><strong style={{ fontWeight: 600 }}>{CHECK_LABELS[c.key] || c.key}.</strong> {c.fix}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-2" style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
              <CheckCircle className="w-4 h-4" /> Clear face, facing the camera, sharp and well lit.
            </div>
          )}
          {result.light && (
            <div className="flex items-start gap-2" style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
              <Sun className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
              <span>The light on your face comes {lightPhrase(result.light.side)}. We'll light the new setting from the same side.</span>
            </div>
          )}
          {bad.length > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
              You can still edit this clip. These are the shots where faces most often change.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ===== Light conflict =====
// Which side the prompt puts its light on, from clauses that name a light source.
// "camera-left" and "on the left" both mean the viewer's left.
const LIGHT_WORDS = /\b(lamps?|light|lights|lit|window|windows|sun|sunlight|spot|spotlight|key|glow|bulbs?|neon|fire|candles?)\b/i;
export function promptLightSide(prompt: string): 'left' | 'right' | null {
  let left = false, right = false;
  for (const clause of prompt.split(/[,;:.]|\band\b/i)) {
    if (!LIGHT_WORDS.test(clause)) continue;
    if (/\bleft\b/i.test(clause)) left = true;
    if (/\bright\b/i.test(clause)) right = true;
  }
  return left && !right ? 'left' : right && !left ? 'right' : null;
}
export const swapLeftRight = (s: string) => s.replace(/\b(left|right)\b/gi, w => {
  const out = /left/i.test(w) ? 'right' : 'left';
  return w[0] === w[0].toUpperCase() ? out[0].toUpperCase() + out.slice(1) : out;
});
export const LightConflictNote: React.FC<{ prompt: string; side: LightSide | null | undefined; onSwap: () => void }> = ({ prompt, side, onSwap }) => {
  if (side !== 'left' && side !== 'right') return null;
  const asked = promptLightSide(prompt);
  if (!asked || asked === side) return null;
  return (
    <div role="note" style={{ margin: '8px 0 0', padding: '8px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
      Your face is lit from the {side} of the frame in this clip, but this description puts the light on the {asked}. You'll look pasted in.
      <button type="button" onClick={onSwap} style={{ ...textBtn, minHeight: 0, padding: '0 0 0 6px' }}>Swap left and right</button>
    </div>
  );
};

// ===== Saved sets =====
export const SavedSetsPicker: React.FC<{
  sets: SceneSet[]; selectedId: string | null; onPick: (s: SceneSet | null) => void;
  onRename: (id: string, name: string) => Promise<void>; onDelete: (id: string) => Promise<void>;
}> = ({ sets, selectedId, onPick, onRename, onDelete }) => {
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!sets.length) return null;
  const selected = sets.find(s => s.id === selectedId) || null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="mono-label" style={{ marginBottom: 8 }}>Your saved sets</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {sets.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => { setEditing(null); setConfirmDelete(null); onPick(selectedId === s.id ? null : s); }}
            className="text-left overflow-hidden"
            style={{ padding: 0, borderRadius: 'var(--hj-r-sm)', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--hj-ink)', background: selectedId === s.id ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)', border: `1px solid ${selectedId === s.id ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}` }}
          >
            <img src={s.thumbUrl} alt="" loading="lazy" className="w-full block" style={{ aspectRatio: '16 / 9', objectFit: 'cover', background: 'var(--hj-surface-3)' }} />
            <div className="truncate" style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>{s.name}</div>
          </button>
        ))}
      </div>
      {selected && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
            Using <strong style={{ fontWeight: 600 }}>{selected.name}</strong>. Make the still below (free) to put this shot in the same room, then preview.
          </p>
          {editing?.id === selected.id ? (
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
              <input value={editing.name} onChange={e => setEditing({ id: selected.id, name: e.target.value.slice(0, 60) })} aria-label="Set name" style={{ ...field, flex: '1 1 180px', width: 'auto' }} />
              <button type="button" disabled={busy || !editing.name.trim()} style={btn(true, busy || !editing.name.trim())}
                onClick={async () => { setBusy(true); try { await onRename(selected.id, editing.name.trim()); setEditing(null); } finally { setBusy(false); } }}>Save name</button>
              <button type="button" style={textBtn} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          ) : confirmDelete === selected.id ? (
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8, fontSize: 13.5, color: 'var(--hj-ink)' }}>
              Delete {selected.name}?
              <button type="button" disabled={busy} style={btn(false, busy)}
                onClick={async () => { setBusy(true); try { await onDelete(selected.id); setConfirmDelete(null); } finally { setBusy(false); } }}>Delete</button>
              <button type="button" style={textBtn} onClick={() => setConfirmDelete(null)}>Keep it</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1" style={{ marginTop: 4 }}>
              <button type="button" style={textBtn} onClick={() => setEditing({ id: selected.id, name: selected.name })}>Rename</button>
              <button type="button" style={textBtn} onClick={() => setConfirmDelete(selected.id)}>Delete</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SaveSetForm: React.FC<{ onSave: (name: string) => Promise<void>; saved: boolean }> = ({ onSave, saved }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (saved) {
    return <div className="flex items-center gap-2" style={{ marginTop: 8, fontSize: 13.5, color: 'var(--hj-ink)' }}><CheckCircle className="w-4 h-4" /> Saved. Pick it under "Your saved sets" on your next clip.</div>;
  }
  if (!open) {
    return (
      <button type="button" style={{ ...btn(false), marginTop: 8 }} onClick={() => setOpen(true)}>
        Save this set for your next shot
      </button>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <div className="flex flex-wrap items-center gap-2">
        <input autoFocus value={name} onChange={e => setName(e.target.value.slice(0, 60))} placeholder="Name it, e.g. Lounge, scene 4" aria-label="Set name" style={{ ...field, flex: '1 1 200px', width: 'auto' }} />
        <button type="button" disabled={busy || !name.trim()} style={btn(true, busy || !name.trim())}
          onClick={async () => { setBusy(true); setErr(''); try { await onSave(name.trim()); } catch (e: any) { setErr(e.message || 'Could not save the set.'); } finally { setBusy(false); } }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save set
        </button>
        <button type="button" style={textBtn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
        Free. Your next clip goes into this same room, so the shots cut together.
      </p>
      {err && <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{err}</p>}
    </div>
  );
};
